<template>
    <section class="order-container">
        <section
            class="section"
            v-if="!isPaymentConfirmed || (isSetupCompleted && isPaymentRefunded) || !orderFullyPaid"
        >
            <div class="container">
                <div class="row">
                    <div class="col-md-6">
                        <h2>{{ $t('shop.pay_now') }}</h2>
                    </div>
                </div>
                <div v-if="paymentFailureMessage" class="row">
                    <div class="col-12">
                        <div class="alert alert-danger" v-html="paymentFailureMessage"></div>
                    </div>
                </div>
                <div class="row order-not-payed-row" ref="paymentRow">
                    <div class="col-md-12 mb-2 payment-col">
                        <Payment
                            :payment="payment"
                            :csrf-token="csrfToken"
                            :order="order"
                            :price="order.price"
                            :payment-methods="paymentMethods"
                            :countries="evidenceConfig.licensePlateCountries"
                            :default-country="payerCountry"
                            :is-performing-request="sendingRequest"
                            :order-invoice-update-url="orderInvoiceUpdateUrl"
                            :is-waiting-for-confirmation="waitingForConfirmation.show"
                            :waiting-for-confirmation-time="waitingFormatted"
                            :guest-orders="guestOrders"
                            :disable-payment-method="disablePaymentMethod"
                            @terminate-subscription="$refs.orderDetails.onTerminatePaymentSubscription()"
                        ></Payment>
                    </div>
                </div>
            </div>
        </section>

        <OrderDetails
            ref="orderDetails"
            v-if="
                ((!isPaymentConfirmed && !isSetupCompleted) || (isSetupCompleted && isPaymentRefunded)) &&
                initialOrder.product_type !== 'combined_order'
            "
            :order="order"
            :order-price="orderPrice"
            :unpaid-order-price="unpaidOrderPrice"
            :is-payment-confirmed="isPaymentConfirmed"
            :is-setup-completed="isSetupCompleted"
            :is-payment-refunded="isPaymentRefunded"
            :show-loading-animation="showLoadingAnimation"
            :successful-completed="success"
            :base-order-active-subscription="baseOrderActiveSubscription"
            :subscription-license="subscriptionLicense"
            :is-completed-view="false"
            :ongoing-order-update="ongoingOrderUpdate"
            :cancel-order-button-config="{
                visible: cancelOrderButton,
                hideButton: hideCancelOrderButton,
                showMessage: cancelOrderShowMessage,
                failed: cancelOrderFailed,
                message: cancelOrderMessage,
            }"
            :order-terminate-subscription-url="orderTerminateSubscriptionUrl"
            :change-license-subscription-url="changeLicenseSubscriptionUrl"
            :has-payment-subscription="hasPaymentSubscription"
            :evidence-config="evidenceConfig"
            :disable-change-buttons="false"
            @cancel-button-clicked="cancelOrder"
            @show-insurance-popup="$refs.insurancePopup.showModal()"
            :insurance-config="insuranceConfig"
            :validity-start-shop-timezone-string="validityStartShopTimezoneString"
            :validity-end-shop-timezone-string="validityEndShopTimezoneString"
        ></OrderDetails>

        <template v-if="initialOrder.product_type === 'combined_order'">
            <combined-order :order="this.initialOrder"></combined-order>
        </template>
        <template v-else>
            <section
            class="section"
            v-if="((isPaymentConfirmed && orderFullyPaid) || (isSetupCompleted && !isPaymentRefunded)) &&
                    initialOrder.product_type !== 'combined_order'
                "
        >
            <div class="container">
                <div class="row mb-4">
                    <div class="col">
                        <GiftVoucherBanner :gift-voucher-christmas-style="giftVoucherChristmasStyle" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-10">
                        <h2 v-if="isVoucherOrder" v-t="'giftVoucher.boughtSuccessful'"></h2>
                        <h2 v-else>
                            <span v-if="order.dispatch_on&& !isSetupCompleted">
                                {{ $t('shop.order_insurance_successful', { setup_date: order.dispatch_on_string }) }}
                            </span>
                                <span v-else>
                                {{ $t('shop.order_download') }}
                            </span>
                            </h2>
                        </div>
                    </div>
                    <div class="row" ref="orderDownloadRow">
                        <div class="col-lg-12 mb-2 padding-reset-desktop">
                            <!-- Status texts below -->
                            <div v-if="isSetupCompleted" class="status-text mb-3 col-md-12 container-mobile-reset">
                                <icon-text-container>
                                    <template v-slot:icon>
                                        <i v-if="success" class="mdi mdi-check status-icon status-icon-success"></i>
                                        <i v-else class="mdi mdi-window-close status-icon status-icon-fail"></i>
                                    </template>

                                    <h4 class="mb-0 mb-mobile mobile-text-center" v-if="success">
                                        {{ $t('shop.success') }}
                                    </h4>
                                    <h4 class="mb-0 mb-mobile mobile-text-center" v-else>{{ $t('shop.fail') }}</h4>
                                    <span
                                        class="text-quote mobile-text-center"
                                        v-if="success"
                                        v-html="$t('order.is_registered_status_text')"
                                    ></span>
                                    <span
                                        class="text-quote mobile-text-center"
                                        v-else
                                        v-html="$t('order.is_not_registered_status_text')"
                                    ></span>
                                </icon-text-container>
                            </div>
                            <div class="payment-success-col" v-if="isPaymentConfirmed || isSetupCompleted">
                                <div v-if="order.dispatch_on && !order.was_dispatched" class="order-dispatch-later">
                                    <div class="col-md-12">
                                        <icon-text-container>
                                            <template v-slot:icon>
                                                <i class="mdi mdi-exclamation status-icon status-icon-warning"></i>
                                            </template>

                                            <div class="h-100 align-items-center">
                                            <span
                                                class="page-title text-quote mobile-text-center"
                                                v-html="
                                                    $t('order.in-progress-on-date', {
                                                        dispatch_date: order.dispatch_on_string,
                                                    })
                                                "
                                            ></span>
                                            </div>
                                        </icon-text-container>
                                    </div>
                                </div>
                                <div
                                v-else-if="
                                    (!finished && !delayedOrderNoticeStatus && !isSetupCompleted) ||
                                    (order.retry_count >= 2 && !delayedOrderNoticeStatus && !isSetupCompleted) ||
                                    (order.retry_count === 1 && delayedOrderNoticeStatus && !isSetupCompleted) ||
                                    (order.retry_count === 0 && delayedOrderNoticeStatus && !isSetupCompleted)
                                "
                                class="order-not-finished"
                            >
                                    <div
                                        v-if="
                                        isCurrentPreorder &&
                                        productPreorderNotice !== null &&
                                        productPreorderNotice !== ''
                                    "
                                        class="col-md-12"
                                    >
                                        <icon-text-container>
                                            <template v-slot:icon>
                                                <i class="mdi mdi-exclamation status-icon status-icon-warning"></i>
                                            </template>

                                        <h4 class="mb-0 mb-mobile mobile-text-center"></h4>
                                        <span
                                            class="text-quote mobile-text-center"
                                            v-html="productPreorderNotice"
                                        ></span>
                                    </icon-text-container>
                                </div>
                                <div
                                    v-else-if="!isVoucherOrder && order.popup_confirmed"
                                    class="col-md-12 padding-reset-loader-desktop"
                                    >
                                    <div class="bg-light rounded p-3 px-4 py-3">
                                        <div class="text-center" v-if="order.retry_count < 2">
                                            {{ $t('order.in-progress') }}
                                        </div>
                                        <div
                                            class="text-center"
                                            v-else-if="order.retry_count >= 2 && !delayedOrderNoticeStatus"
                                        >
                                            {{ $t('order.in-progress-second') }}
                                        </div>

                                        <loading-animation
                                            v-if="order.retry_count < 2"
                                            preloader-container-id=""
                                            status-container-id=""
                                            :horizontal-centered="true"
                                            :visible="true"
                                        ></loading-animation>
                                    </div>
                                </div>
                                <div
                                    v-else-if="!isVoucherOrder && !order.popup_confirmed"
                                    class="col-md-12 padding-reset-loader-desktop"
                                >
                                    <div class="bg-light rounded p-3 px-4 py-3">
                                        <div class="text-center">{{ $t('order.awaiting-confirmation') }}</div>
                                        <loading-animation
                                            preloader-container-id=""
                                            status-container-id=""
                                            :horizontal-centered="true"
                                            :visible="true"
                                        ></loading-animation>
                                    </div>
                                </div>
                                <div v-else-if="isVoucherOrder" class="col-md-12 padding-reset-loader-desktop">
                                    <icon-text-container>
                                        <template v-slot:icon>
                                            <i class="mdi mdi-check status-icon status-icon-success"></i>
                                        </template>

                                            <h4 class="mb-0 mb-mobile mobile-text-center">
                                                {{ $t('giftVoucher.success') }}
                                            </h4>

                                            <span
                                                class="text-quote mobile-text-center"
                                                v-html="$t('giftVoucher.successText')"
                                            ></span>
                                        </icon-text-container>
                                    </div>
                                </div>
                                <div v-else class="order-info">
                                    <div v-if="order.was_successful" class="order-was-successful">
                                        <div class="row align-items-center no-margin">
                                            <div class="col-md-12 text-center container-mobile-reset">
                                                <div class="bg-light rounded px-4 py-3">
                                                    <span class="text-quote">{{ $t('shop.order_summary') }}</span>
                                                    <h4 class="mt-2">{{ order.product.name }}</h4>
                                                    <p class="mt-2 mb-0">{{ $t('generic.price') }}</p>
                                                    <h2 class="mb-2 reset-price">{{ orderPrice.toFixed(2) }} €</h2>

                                                    <img
                                                        :src="'/' + order.barcode_path"
                                                        alt="Vignette Barcode"
                                                        class="mw-100"
                                                    />

                                                    <div class="mt-3 d-flex flex-col flex-md-row justify-center flex-gap-1">
                                                        <a
                                                            :href="invoiceDownloadUrl"
                                                            target="_blank"
                                                            class="btn btn-sm btn-primary my-1"
                                                            v-if="order.invoice_wanted"
                                                            style="font-size: 1rem"
                                                        >
                                                            <i class="mdi mdi-download mr-1"></i>
                                                            {{ $t('order.download-invoice') }}
                                                        </a>
                                                        <a
                                                            :href="confirmationDownloadUrl"
                                                            target="_blank"
                                                            class="btn btn-sm btn-primary my-1"
                                                            style="font-size: 1rem"
                                                        >
                                                            <i class="mdi mdi-download mr-1"></i>
                                                            {{ $t('order.download-confirmation') }}
                                                        </a>
                                                        <a
                                                            v-if="
                                                            typeof order.wallet_filename === 'string' &&
                                                            order.wallet_filename.length
                                                        "
                                                            :href="walletFileDownloadUrl"
                                                            target="_blank"
                                                            class="btn btn-sm btn-primary my-1"
                                                            style="font-size: 1rem"
                                                        >
                                                            <i class="mdi mdi-download mr-1"></i>
                                                            {{ $t('order.downloadWallet') }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                <!-- /SUCCESSFUL-ORDER -->
                                <div
                                    v-else-if="
                                        delayedOrderNoticeStatus && !order.was_successful && order.retry_count >= 3
                                        "
                                    class="order-has-error"
                                >
                                    <div class="alert alert-danger mb-0">
                                        <i class="mdi mdi-alert-circle"></i>
                                        <span v-html="$t('order.finish-notice-error')"></span>
                                    </div>
                                </div>
                                <!-- /CONTAINS-ERROR -->

                                <div
                                    v-if="delayedOrderNoticeStatus && order.retry_count === 2 && !order.was_successful"
                                    class="mb-0 order-has-error"
                                    >
                                    <div class="alert alert-warning mb-0">
                                        <i class="mdi mdi-alert-circle"></i>
                                        <span v-html="$t('order.delayedSetup')"></span>
                                    </div>
                                </div>
                                <!-- /DELAYED-ORDER -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section></template>

        <section
            class="section"
            v-if="
                !this.order.canceled &&
                this.isPaymentConfirmed &&
                !this.isPaymentRefunded &&
                order.was_successful !== false &&
                trustpilotConfig.cashbackEnabled
            "
        >
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <h4 class="mb-0 mb-mobile mobile-text-center text-center">
                                {{ $t('order.trustpilotTitle') }}
                        </h4>
                    </div>
                </div>
                <div class="mt-3 row">
                    <div class="col-12 col-sm-12 col-md-12 col-lg-6 mx-auto">
                        <div class="bg-light rounded px-4 py-3 border-primary align-items-center">
                            <div class="row mb-2">
                                <div class="col-12">
                                    {{ $t('order.trustpilotText') }}
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-sm-12 col-md-6 trustpilot-btn-overlay">
                                    <button
                                        @click.prevent="handleTrustpilotPopup"
                                        class="btn btn-primary btn-block"
                                    >
                                        {{ $t('order.trustpilotButton') }}
                                    </button>
                                </div>
                                <div class="col-12 col-sm-12 col-md-6">
                                    <ResourcePicture
                                        resource-name="/assets/img/trustpilot_logo.png"
                                        class="trustpilot-img img-fluid"
                                        alt="Trustpilot"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="section" v-if="shopAdverts.length > 0 && initialOrder.product_type !== 'combined_order'">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <h4 class="mb-0 mb-mobile mobile-text-center">
                            {{ $t('shop.advert_title') }}
                        </h4>
                        <span class="text-quote mobile-text-center">
                            {{ $t('shop.advert_description') }}
                        </span>
                    </div>
                </div>
                <div class="mt-3 row">
                    <div class="col-12">
                        <div class="bg-light rounded px-4 py-3">
                            <div class="row">
                                <div
                                    v-for="(shopAdvert, index) in shopAdverts"
                                    :key="shopAdvert.language_line_shop_name"
                                    :class="
                                        shopAdverts.length > 1
                                            ? index === 1
                                                ? 'col-md-6 mt-md-0 mt-3'
                                                : index > 1
                                                  ? 'col-md-6 mt-md-3 mt-3'
                                                  : 'col-md-6'
                                            : 'col-md-12 text-center'
                                    "
                                >
                                    <template v-if="shopAdverts.length > 1">
                                        <div class="row">
                                            <div class="col-3 col-md-4 text-right mobile-shop-adverts-text">
                                                <a target="_blank" :href="shopAdvert.url"
                                                    ><img
                                                        width="40"
                                                        height="24"
                                                        :src="shopAdvert.file"
                                                        :alt="$t(shopAdvert.language_line_shop_name)"
                                                /></a>
                                            </div>
                                            <div class="col-9 col-md-8 text-left">
                                                <span class="text-quote mobile-shop-adverts-text">
                                                    <a
                                                        target="_blank"
                                                        :href="shopAdvert.url + '/' + $language"
                                                        v-html="$t(shopAdvert.language_line_shop_name)"
                                                    ></a>
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <img
                                            width="40"
                                            height="24"
                                            :src="shopAdvert.file"
                                            :alt="$t(shopAdvert.language_line_shop_name)"
                                        />
                                        <span class="text-quote mobile-shop-adverts-text">
                                            <a
                                                target="_blank"
                                                :href="shopAdvert.url + '/' + $language"
                                                v-html="$t(shopAdvert.language_line_shop_name)"
                                            ></a>
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="section" v-if="!isVoucherOrder && initialOrder.product_type !== 'combined_order'">
            <div class="container">
                <div class="row">
                    <div v-if="cartConversionTracking" class="cart-conversion-tracking-container">
                        <div id="order-cart-conversion"></div>
                    </div>
                    <div v-if="bingConversionTracking" class="bing-conversion-tracking-container">
                        <div id="order-bing-conversion"></div>
                    </div>

                    <div v-if="evidenceConfig.show" class="col-md-6">
                        <section v-if="isPaymentConfirmed || isSetupCompleted">
                            <div class="container-mobile-reset">
                                <div class="row">
                                    <div class="col-md-12 padding-reset-headline-desktop">
                                        <h2>{{ $t('shop.order_check') }}</h2>
                                    </div>
                                </div>
                                <div class="row payment-success-col">
                                    <div class="col-md-12 mb-2 container-mobile-reset">
                                        <div class="bg-light rounded p-3">
                                            <EvidenceContainer
                                                :show-order-buttons="evidenceConfig.showOrderButtons"
                                                :license-plate-countries="evidenceConfig.licensePlateCountries"
                                                :route-toll-urls="evidenceConfig.routeTollUrls"
                                                :default-license-plate-country="
                                                    evidenceConfig.defaultLicensePlateCountry
                                                "
                                                :default-license-plate="order.license"
                                                :small-license-plate="false"
                                                :hide-route-toll-products="hideRouteTollProducts"
                                            ></EvidenceContainer>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                    <div v-if="typeof hideRouteTollProducts !== 'boolean' || !hideRouteTollProducts" class="col-md-6">
                        <section v-if="(isPaymentConfirmed || isSetupCompleted) && order.product_type === 'vignette'">
                            <div class="container-mobile-reset">
                                <div class="row">
                                    <div class="col-md-12 padding-reset-headline-desktop">
                                        <h2 class="reset-margin">{{ $t('shop.routetoll_maybe_required') }}</h2>
                                    </div>
                                </div>
                                <div class="row route-toll-warning-col">
                                    <div class="col-md-12 mb-2">
                                        <icon-text-container>
                                            <template v-slot:icon>
                                                <i class="mdi mdi-exclamation status-icon status-icon-warning"></i>
                                            </template>

                                            <h4 class="mb-0 mb-mobile mobile-text-center">
                                                {{ $t('shop.attention_route_toll') }}
                                            </h4>
                                            <span
                                                class="d-block text-quote mobile-text-center"
                                                v-html="$t('shop.routetoll_maybe_required_description')"
                                            ></span>
                                            <a
                                                :href="$t('frontend.tollcalculator_url')"
                                                target="_blank"
                                                class="btn btn-primary mt-4"
                                                >{{ $t('shop.check_now') }}</a
                                            >
                                        </icon-text-container>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            <div v-if="conversionTracking" class="conversion-tracking-container">
                <div id="order-conversion"></div>
            </div>
        </section>

        <OrderDetails
            v-if="(isPaymentConfirmed || isSetupCompleted) && initialOrder.product_type !== 'combined_order'"
            :order="order"
            :order-price="orderPrice"
            :unpaid-order-price="unpaidOrderPrice"
            :is-payment-confirmed="isPaymentConfirmed"
            :is-setup-completed="isSetupCompleted"
            :base-order-active-subscription="baseOrderActiveSubscription"
            :subscription-license="subscriptionLicense"
            :is-payment-refunded="isPaymentRefunded"
            :show-loading-animation="showLoadingAnimation"
            :successful-completed="success"
            :is-completed-view="true"
            :is-transfer-of-product-enabled="isTransferOfProductEnabled"
            :ongoing-order-update="ongoingOrderUpdate"
            :evidence-config="evidenceConfig"
            :order-terminate-subscription-url="orderTerminateSubscriptionUrl"
            :change-license-subscription-url="changeLicenseSubscriptionUrl"
            :has-payment-subscription="hasPaymentSubscription"
            :disable-change-buttons="disableChangeButtons"
            @order-updated="order = $event"
            @show-cancellation-popup="$refs.cancelOrderPopup.showModal()"
            :validity-start-shop-timezone-string="validityStartShopTimezoneString"
            :validity-end-shop-timezone-string="validityEndShopTimezoneString"
        ></OrderDetails>

        <section class="section" v-if="order.invoice_wanted">
            <div class="container">
                <div class="row">
                    <div class="col-md-6">
                        <h2>{{ $t('shop.invoice_details') }}</h2>
                    </div>
                </div>
                <div class="row invoice-col">
                    <div class="col-md-12 mb-2">
                        <div class="bg-light rounded px-4 py-3">
                            <table class="table table-borderless table-sm mb-0">
                                <tbody>
                                    <tr>
                                        <td class="label">{{ $t('shop.first_name') }}</td>
                                        <td v-if="order.first_name" class="value text-right">{{ order.first_name }}</td>
                                        <td v-else-if="order.paypal_first_name" class="value text-right">
                                            {{ order.paypal_first_name }}
                                        </td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                    <tr>
                                        <td class="label">{{ $t('shop.last_name') }}</td>
                                        <td v-if="order.last_name" class="value text-right">{{ order.last_name }}</td>
                                        <td v-else-if="order.paypal_last_name" class="value text-right">
                                            {{ order.paypal_last_name }}
                                        </td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                    <tr>
                                        <td class="label">{{ $t('shop.street') }}</td>
                                        <td v-if="order.street" class="value text-right">{{ order.street }}</td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                    <tr>
                                        <td class="label">{{ $t('shop.house_number') }}</td>
                                        <td v-if="order.house" class="value text-right">{{ order.house }}</td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                    <tr>
                                        <td class="label">{{ $t('shop.postcode') }}</td>
                                        <td v-if="order.postcode" class="value text-right">{{ order.postcode }}</td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                    <tr>
                                        <td class="label">{{ $t('shop.city') }}</td>
                                        <td v-if="order.city" class="value text-right">{{ order.city }}</td>
                                        <td v-else class="value text-right">&ndash;</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <!-- /INVOICE -->
        </section>

        <Revocation
            :order-completed="isSetupCompleted"
            :button-visible="revokeAbleByCustomer"
            :order="order"
            :revocation-url="revocationUrl"
            :invoice-download-url="invoiceDownloadUrl"
        ></Revocation>
        <app-promo-popup
            :is-payment-confirmed="isPaymentConfirmed"
            :show-app-promo-popup="showAppPromoPopup"
            ref="appPromoModal"
        ></app-promo-popup>
        <BirthdayVoucherPopup
            :order="order"
            :birthday-voucher-config="birthdayVoucherConfig"
            :is-payment-confirmed="isPaymentConfirmed"
            :show-popup="showBirthdayVoucherPopup"
            ref="birthdayVoucherPopup"
        ></BirthdayVoucherPopup>
        <SetupPopup
            :config="setupPopupConfig"
            :order="order"
            :license-plate-countries="evidenceConfig.licensePlateCountries"
            :default-license-plate-country="evidenceConfig.defaultLicensePlateCountry"
        ></SetupPopup>
        <InsurancePopup
            ref="insurancePopup"
            :is-payment-confirmed="isPaymentConfirmed"
            :insurance-config="insuranceConfig"
            :order="order"
            v-if="insuranceConfig.enabled"
        ></InsurancePopup>
        <CancelOrderPopup
            ref="cancelOrderPopup"
            :is-payment-confirmed="isPaymentConfirmed"
            :cancel-order-config="cancelOrderConfig"
            :order="order"
        ></CancelOrderPopup>
        <TrustpilotPopup
            ref="trustpilotPopup"
            :csrf-token="csrfToken"
            :is-payment-confirmed="isPaymentConfirmed"
            :trustpilot-config="trustpilotConfig"
            :order="order"
            v-if="trustpilotConfig.cashbackEnabled"
        ></TrustpilotPopup>

        <UpdateSubscription :config="updateSubscriptionConfig" />
    </section>
    <!-- /ORDER-CONTAINER -->
</template>

<style scoped>
.flex-gap-1 {
    gap: 1rem;
}

.order-not-payed-row {
    position: relative;
}

.order-not-payed-row .loader-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99999;

    width: 100%;
    height: 100%;

    background-color: rgba(255, 255, 255, 0.4);
}

.order-not-payed-row .loader-container #status {
    position: fixed !important;
}

.trustpilot-img {
    width: 12rem;
    height: auto;
}

.trustpilot-img :deep(img) {
    max-width: 100%;
    height: auto;
}

.trustpilot-btn-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
}

.payment-container-card {
    margin-bottom: 20px;
}

.loader-container {
    position: relative;
    height: 100px;
}

#preloader {
    position: absolute;
    background: transparent;
}

#preloader.always-show,
#preloader .always-show {
    display: block !important;
}
</style>

<script>
import Payment from './Order/Payment.vue';
import { echo } from '../echo.js';
import OrderDetails from './Order/Details.vue';
import GiftVoucherBanner from './GiftVoucherBanner.vue';
import ResourcePicture from './ResourcePicture.vue';
import Revocation from './Order/Revocation.vue';
import BirthdayVoucherPopup from './Order/BirthdayVoucherPopup.vue';
import SetupPopup from './Order/SetupPopup.vue';
import InsurancePopup from './Order/InsurancePopup.vue';
import CancelOrderPopup from './Order/CancelOrderPopup.vue';
import TrustpilotPopup from './Order/TrustpilotPopup.vue';
import UpdateSubscription from './Order/Payment/UpdateSubscription.vue';
import EvidenceContainer from './EvidenceChecker/Container.vue';
import CombinedOrder from './Order/CombinedOrder.vue';
import AppPromoPopup from './Order/AppPromoPopup.vue';
import LoadingAnimation from './LoadingAnimation.vue';

export default {
    components: {
        UpdateSubscription,
        TrustpilotPopup,
        CancelOrderPopup,
        InsurancePopup,
        SetupPopup,
        BirthdayVoucherPopup,
        Revocation,
        ResourcePicture,
        CombinedOrder,
        GiftVoucherBanner,
        OrderDetails,
        Payment,
        EvidenceContainer,
        AppPromoPopup,
        LoadingAnimation,
    },

    provide() {
        return {
            isVoucherOrder: this.isVoucherOrder,
            giftVoucherPayments: this.giftVoucherPayments,
            giftVoucherProductUrl: this.giftVoucherProductUrl,
            contactUrl: this.contactUrl,
        };
    },

    beforeUnmount() {
        if (typeof Echo !== 'object' || typeof echo.leave !== 'function') {
            return;
        }

        echo.leave('orders.' + this.order.hash.replace(/-/g, '_'));
        echo.leave('orders.delayed.setup');
    },

    mounted() {
        // Need regex here because a "replace all" is not implemented in most of the browser :shrug:
        const channelName = 'orders.' + this.order.hash.replace(/-/g, '_');

        if (this.waitingForConfirmation.show) {
            this.updateWaitingForConfirmationSeconds(this.waitingForConfirmation.secondsToWait);

            this.waitingIntervalId = setInterval(() => {
                this.updateWaitingForConfirmationSeconds(this.waitingSecondsLeft - 1);
            }, 1000);
        }

        echo.private(channelName)
            .listen('OrderUpdated', (event) => {
                if (event.orderChanges !== null) {
                    this.order = Object.assign(this.order, event.orderChanges);
                }

                if (event.paymentChanges !== null) {
                    this.payment = Object.assign(this.payment, event.paymentChanges);
                }
            })
            .listen('DelayedPaymentConfirmation', () => {
                if (this.waitingIntervalId === -1) {
                    clearInterval(this.waitingIntervalId);
                    this.waitingIntervalId = -1;
                }

                window.location.href = location.href;
            });

        echo.channel('orders.delayed.setup').listen('.status.updated', (info) => {
            this.delayedOrderNoticeStatus = info.status;
        });

        if (typeof this.order === 'object' && this.order !== null && typeof this.order.country_code !== 'undefined') {
            this.payerCountry = this.order.country_code || '';
        }

        this.$nextTick(() => {
            // Default scroll value
            let scroll = null;

            // In the case that the payment row is visible, we have to scroll this item into focus.
            if (this.$refs.paymentRow) {
                scroll = this.getScrollTop('paymentRow');
            } else if (this.$refs.orderDownloadRow) {
                scroll = this.getScrollTop('orderDownloadRow');
            }

            // In the case that we have a scroll value, we have to scroll this element into the
            // top corner view of browser.
            if (scroll !== null && scroll > 0) {
                setTimeout(() => {
                    window.scrollTo(0, scroll);
                }, 50);
            }
        });
    },
    props: [
        'shopAdverts',
        'subscriptionLicense',
        'baseOrderActiveSubscription',
        'initialPayment',
        'initialOrder',
        'invoiceDownloadUrl',
        'confirmationDownloadUrl',
        'cancelOrderButton',
        'cancelOrderUrl',
        'revokeAbleByCustomer',
        'evidenceConfig',
        'paymentMethods',
        'csrfToken',
        'isTransferOfProductEnabled',
        'walletFileDownloadUrl',
        'setupPopupConfig',
        'birthdayVoucherConfig',
        'cancelOrderConfig',
        'insuranceConfig',
        'trustpilotConfig',
        'conversionTrackedUrl',
        'bingConversionTrackedUrl',
        'cartConversionTrackedUrl',
        'sklikConversionId',
        'revocationUrl',
        'orderInvoiceUpdateUrl',
        'hideRouteTollProducts',
        'initialDelayedOrderNoticeStatus',
        'ongoingOrderUpdate',
        'orderTerminateSubscriptionUrl',
        'changeLicenseSubscriptionUrl',
        'hasPaymentSubscription',
        'waitingForConfirmation',
        'guestOrders',
        'disableChangeButtons',
        'paymentFailureMessage',
        'disablePaymentMethod',
        'updateSubscriptionConfig',
        'giftVoucherPayments',
        'giftVoucherProductUrl',
        'giftVoucherChristmasStyle',
        'validityStartShopTimezoneString',
        'validityEndShopTimezoneString',
        'showAppPromoPopup',
        'contactUrl',
    ],
    data() {
        return {
            updatedPayment: null,
            updatedOrder: null,
            sendingRequest: false,
            paymentMethod: null,
            paymentMethodSelectionAvailable: true,
            hideCancelOrderButton: false,
            cancelOrderShowMessage: false,
            cancelOrderFailed: false,
            cancelOrderMessage: '',
            showLoadingAnimation: false,
            showBirthdayVoucherPopup: this.birthdayVoucherConfig.showPopup,
            payerCountry: '',
            delayedOrderNoticeStatus:
                typeof this.initialDelayedOrderNoticeStatus === 'boolean' && this.initialDelayedOrderNoticeStatus,
            waitingIntervalId: -1,
            waitingSecondsLeft: 0,
            waitingFormatted: '00:00:00',
        };
    },
    computed: {
        /**
         * Determine the current price of the order.
         *
         * @returns {Number}
         * */
        orderPrice() {
            const dunningFee = typeof this.order.dunning_fee === 'number' ? this.order.dunning_fee : 0;

            return this.order.price + dunningFee;
        },

        unpaidOrderPrice() {
            return this.order.unpaid_amount;
        },

        wasDispatched() {
            return (
                typeof this.order.was_dispatched !== 'undefined' &&
                ['true', true, '1', 1].indexOf(this.order.was_dispatched) !== -1
            );
        },

        finished() {
            // In the case that the order was already successfully processed, we can give
            // the user the information about the fetched details.
            if (
                this.order.was_successful !== null &&
                [true, 'true', 1, '1'].indexOf(this.order.was_successful) !== -1
            ) {
                return true;
            }

            return this.wasDispatched && this.order.was_successful !== null;
        },
        order: {
            get() {
                return this.updatedOrder !== null ? this.updatedOrder : this.initialOrder;
            },
            set(value) {
                this.updatedOrder = value;
            },
        },
        payment: {
            get() {
                return this.updatedPayment !== null ? this.updatedPayment : this.initialPayment;
            },
            set(value) {
                this.updatedPayment = value;
            },
        },

        isPaymentConfirmed() {
            return !!(this.payment && !this.isPaymentRefunded && (this.payment.succeeded || this.payment.is_confirmed));
        },

        isPaymentRefunded() {
            return this.payment && (this.payment.was_refunded || this.payment.wasRefunded || false);
        },

        isSetupCompleted() {
            return (this.order.was_successful !== null && this.order.was_successful) ?? false;
        },

        orderFullyPaid() {
            return this.order.is_fully_paid;
        },

        isCurrentPreorder() {
            if (typeof this.order.is_preorder !== 'boolean' || !this.order.is_preorder) {
                return false;
            }

            return this.order.validity_start === this.order.product.preorder_validity_from;
        },

        productIsInPreorderMode() {
            if (typeof this.order.product.is_preorder !== 'boolean' || !this.order.product.is_preorder) {
                return false;
            }

            return true;
        },

        productPreorderNotice() {
            return this.isCurrentPreorder ? this.order.product.preorder_notice : null;
        },

        success() {
            return this.finished && this.order.was_successful;
        },

        /* eslint-disable */
        cartConversionTracking() {
            if (!this.order.canceled && !this.order.cart_conversion_tracked && !this.isPaymentConfirmed) {
                this.$nextTick().then(() => {
                    this.getCartConversionTracking;
                });

                return true;
            }
            return false;
        },

        getCartConversionTracking() {
            // can track conversion
            if (!this.order.cart_conversion_tracked) {
                // save the tracking
                this.order.cart_conversion_tracked = true;
                this.setOrderTrackingFlag('cart');

                // create script tag
                let script = document.createElement('script');
                script.type = 'text/javascript';
                script.text =
                    'let cartTracked = false; ' +
                    "document.addEventListener('cart.loaded', function() { " +
                    'if (cartTracked) { return; }' +
                    "cartTracked = true; console.log('track the cart for bing..');" +
                    'window.uetq = window.uetq || [];' +
                    "window.uetq.push('event', 'add_to_cart', {});" +
                    '});';

                const scripts = document.querySelectorAll('#order-cart-conversion script');

                for (let i = 0; i < scripts.length; i++) {
                    scripts[i].remove();
                }

                document.querySelector('#order-cart-conversion').append(script);

                // Define an interval to handle the check of bing extension, to trigger the tracking of order manually.
                const cartConvInterval = setInterval(function () {
                    if (typeof window.uetq == 'undefined') {
                        return;
                    }

                    clearInterval(cartConvInterval);

                    document.dispatchEvent(new Event('cart.loaded'));
                }, 100);
            }
        },

        bingConversionTracking() {
            if (
                !this.order.canceled &&
                !this.order.bing_conversion_tracked &&
                this.isPaymentConfirmed &&
                !this.isPaymentRefunded
            ) {
                this.$nextTick().then(() => {
                    this.getBingConversionTrackingCode;
                });

                return true;
            }
            return false;
        },

        getBingConversionTrackingCode() {
            // can track conversion
            if (!this.order.bing_conversion_tracked) {
                // save the tracking
                this.order.bing_conversion_tracked = true;
                this.setOrderTrackingFlag('bing');

                // create script tag
                let script = document.createElement('script');
                script.type = 'text/javascript';
                script.text =
                    'let bingTracked = false; ' +
                    "document.addEventListener('bing.loaded', function() { " +
                    'if (bingTracked) { return; }' +
                    "bingTracked = true; console.log('track the order for bing..');" +
                    'window.uetq = window.uetq || [];' +
                    'window.uetq.push("event", "purchase", {});' +
                    'window.uetq.push(' +
                    '"event",' +
                    '"PRODUCT_PURCHASE",' +
                    '{' +
                    '"ecomm_prodid": "' +
                    (this.order.product_type + '-' + this.order.option_id) +
                    '",' +
                    '"ecomm_pagetype":"PURCHASE"' +
                    '}' +
                    ');' +
                    'var conversionConf = {' +
                    'id: ' +
                    this.sklikConversionId +
                    ',' +
                    'value: 230' +
                    "}; console.log('track the order for sklik ...');" +
                    'if (window.rc && window.rc.conversionHit) {' +
                    'window.rc.conversionHit(conversionConf);' +
                    '}' +
                    '});';

                const scripts = document.querySelectorAll('#order-bing-conversion script');

                for (let i = 0; i < scripts.length; i++) {
                    scripts[i].remove();
                }

                document.querySelector('#order-bing-conversion').append(script);

                // Define an interval to handle the check of bing extension, to trigger the tracking of order manually.
                const bingConvInterval = setInterval(function () {
                    if (typeof window.uetq == 'undefined') {
                        return;
                    }

                    clearInterval(bingConvInterval);

                    document.dispatchEvent(new Event('bing.loaded'));
                }, 100);
            }
        },

        conversionTracking() {
            if (
                !this.order.canceled &&
                !this.order.conversion_tracked &&
                this.isPaymentConfirmed &&
                !this.isPaymentRefunded
            ) {
                this.$nextTick().then(() => {
                    this.getConversionTrackingCode;
                });

                return true;
            }
            return false;
        },

        getConversionTrackingCode() {
            // can track conversion
            if (!this.order.conversion_tracked) {
                // save the tracking
                this.order.conversion_tracked = true;
                this.setOrderTrackingFlag('google');

                // name of item
                let name = '';

                // Switch the product type to handle the read of item name correctly.
                switch (this.order.product_type) {
                    case 'vignette':
                        name =
                            'vignette - ' +
                            this.order.product.duration +
                            (this.order.product.is_month_duration ? ' Monat(e)' : ' Tage') +
                            ' - ' +
                            this.order.option.vehicle_type;

                        break;

                    case 'route_toll':
                        name =
                            'route-toll - ' +
                            this.order.product.tracking_identifier +
                            ' - ' +
                            this.order.option.tracking_identifier;

                        break;
                }

                // create script tag
                let script = document.createElement('script');
                script.type = 'text/javascript';
                script.text =
                    'let tracked = false; ' +
                    "document.addEventListener('gtag.loaded', function() { " +
                    'if (tracked) { return; }' +
                    "tracked = true; console.log('track the order..');" +
                    "gtag('event', 'purchase', {" +
                    '"transaction_id": \'' +
                    this.order.id +
                    "'," +
                    '"value": \'' +
                    this.orderPrice.toFixed(2) +
                    "'," +
                    '"currency": \'EUR\',' +
                    '"tax": \'0\',' +
                    '"shipping": \'0\',' +
                    '"items": [{' +
                    '"id": \'' +
                    (this.order.product_type + '-' + this.order.option_id) +
                    "'," +
                    '"name": \'' +
                    name +
                    "'," +
                    '"category": \'' +
                    this.order.product_type +
                    "'," +
                    '"quantity": 1,' +
                    '}]' +
                    '})' +
                    '});';

                const scripts = document.querySelectorAll('#order-conversion script');

                for (let i = 0; i < scripts.length; i++) {
                    scripts[i].remove();
                }

                document.querySelector('#order-conversion').append(script);

                // Define a interval to handle the check of gtag, to trigger the tracking of order manually.
                let interval = setInterval(() => {
                    // The gtag isn't loaded yet, we don't have to do anything here.
                    if (typeof window.gtag !== 'function') {
                        return;
                    }

                    // Clear the interval and dispatch event to trigger the tracking.
                    clearInterval(interval);
                    interval = -1;

                    document.dispatchEvent(new Event('gtag.loaded'));
                }, 100);
            }
        },
        /* eslint-enable */

        isVoucherOrder() {
            return this.order.type === 'voucher_order';
        },
    },
    methods: {
        /**
         * @param {number} totalSeconds
         *
         * @returns {void}
         */
        updateWaitingForConfirmationSeconds(totalSeconds) {
            this.waitingSecondsLeft = totalSeconds;

            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds - hours * 3600) / 60);
            const seconds = Math.floor(totalSeconds - hours * 3600 - minutes * 60);
            const parts = [];

            if (hours > 0) {
                parts.push((hours > 9 ? '' : '0') + hours);
            }

            if (minutes > 0) {
                parts.push((minutes > 9 ? '' : '0') + minutes);
            }

            if (seconds > 0) {
                parts.push((seconds > 9 ? '' : '0') + seconds);
            }

            this.waitingFormatted = parts.join(':');

            // In the case that the left time is zero or below, we have to cancel the interval
            // and show the payment form again. Otherwise, the user has to reload the page manually.
            if (totalSeconds <= 0 && this.waitingIntervalId !== -1) {
                clearInterval(this.waitingIntervalId);
                this.waitingIntervalId = -1;

                // To avoid that customers may create a duplicate subscription for this order,
                // we have to reload the page. This should make sure that the view will be updated
                // correctly in any case.
                window.location.href = location.href;
            }
        },

        convertRemToPixels(rem) {
            return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
        },
        getScrollTop(ref) {
            if (!this.$refs[ref]) {
                return null;
            }

            /* @type {HTMLElement} element */
            const element = this.$refs[ref];
            const rect = element.getBoundingClientRect();

            return rect.top + window.pageYOffset - window.innerHeight / 2;
        },
        changeSendingRequest(sending) {
            this.sendingRequest = sending;
        },

        handleTrustpilotPopup() {
            this.$refs.trustpilotPopup.showModal();
        },

        /**
         * Update the conversion tracked flag of order, to prevent further tracking of the same order.
         *
         * @returns {void}
         */
        setOrderTrackingFlag(type) {
            let url,
                alias = '';

            switch (type) {
                case 'bing':
                    url = this.bingConversionTrackedUrl;
                    alias = 'bing_';

                    break;

                case 'cart':
                    url = this.cartConversionTrackedUrl;
                    alias = 'cart_';

                    break;

                case 'google':
                    url = this.conversionTrackedUrl;

                    break;
            }

            axios.post(url).then((response) => {
                // In the case the request failed, we don't have to update the flag. Because
                // we can't be sure that the order was really tracked or not.
                if (!response.data.success) {
                    return;
                }

                this.order[alias + 'conversion_tracked'] = true;
            });
        },

        /**
         * Handle the canceling of the order.
         *
         * @returns {void}
         */
        cancelOrder() {
            // button is disabled
            if (!this.cancelOrderButton) {
                return;
            }

            // hide message from previous request
            this.cancelOrderShowMessage = false;
            this.cancelOrderMessage = '';
            this.cancelOrderFailed = false;
            this.showLoadingAnimation = true;

            // send the request
            axios
                .post(this.cancelOrderUrl)
                .then((response) => {
                    this.cancelOrderMessage = response.data.message;

                    if (response.data.success) {
                        this.hideCancelOrderButton = true;
                        setTimeout(function () {
                            window.location.href = response.data.redirect;
                        }, response.data.wait || 0);
                    } else {
                        this.cancelOrderFailed = true;
                    }

                    // show the message
                    this.cancelOrderShowMessage = true;
                })
                .finally(() => {
                    this.showLoadingAnimation = false;
                });
        },
    },
};
</script>
